import { makeStyles } from "@mui/styles";
import { THEME_COLORS } from "../../context/constants";

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    minHeight: "80vh",
    fontFamily: "Lexend, sans-serif",
    padding: "20px",
  },
  // Past generations carousel
  carouselArea: {
    width: "100%",
    maxWidth: "860px",
    marginBottom: "24px",
  },
  carouselHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px",
  },
  carouselScroll: {
    display: "flex",
    gap: "12px",
    overflowX: "auto" as const,
    paddingBottom: "8px",
    scrollbarWidth: "thin" as const,
    "&::-webkit-scrollbar": {
      height: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#ddd",
      borderRadius: "4px",
    },
  },
  videoCard: {
    minWidth: "180px",
    maxWidth: "180px",
    borderRadius: "12px",
    overflow: "hidden",
    backgroundColor: "#f9f9f9",
    border: "1px solid #eee",
    position: "relative" as const,
    flexShrink: 0,
    cursor: "pointer",
    transition: "box-shadow 0.2s",
    "&:hover": {
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
    "& video": {
      width: "100%",
      height: "120px",
      objectFit: "cover" as const,
      display: "block",
    },
  },
  videoCardOverlay: {
    height: "120px",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f5f5f5",
    gap: "4px",
  },
  videoCardInfo: {
    padding: "8px 10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "4px",
  },
  videoCardPrompt: {
    fontSize: "11px",
    color: "#666",
    fontFamily: "Lexend, sans-serif",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
    flex: 1,
    minWidth: 0,
  },
  // Main form card
  formCard: {
    width: "100%",
    maxWidth: "860px",
    borderRadius: "20px",
    border: "1px solid #e0e0e0",
    backgroundColor: "#fff",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    padding: "20px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
  },
  // Avatar + Style row
  configRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap" as const,
  },
  selectorLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#888",
    fontFamily: "Lexend, sans-serif",
    marginBottom: "4px",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
  },
  avatarSelector: {
    display: "flex",
    flexDirection: "column" as const,
  },
  avatarButton: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "8px 14px",
    borderRadius: "12px",
    border: "1px solid #e0e0e0",
    backgroundColor: "#fafafa",
    cursor: "pointer",
    transition: "border-color 0.15s, background-color 0.15s",
    fontFamily: "Lexend, sans-serif",
    "&:hover": {
      borderColor: THEME_COLORS.yellow,
      backgroundColor: THEME_COLORS.lightYellow3,
    },
  },
  avatarImage: {
    width: "36px",
    height: "36px",
    borderRadius: "8px",
    objectFit: "cover" as const,
    backgroundColor: "#eee",
    flexShrink: 0,
  },
  avatarPlaceholder: {
    width: "36px",
    height: "36px",
    borderRadius: "8px",
    backgroundColor: "#f0f0f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: "#bbb",
  },
  avatarName: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#222",
    fontFamily: "Lexend, sans-serif",
  },
  configField: {
    display: "flex",
    flexDirection: "column" as const,
    minWidth: "140px",
  },
  modeToggleGroup: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: "10px",
    marginBottom: "16px",
  },
  modeButton: {
    textTransform: "none" as const,
    borderRadius: "10px",
    padding: "8px 18px",
    fontFamily: "Lexend, sans-serif",
    fontWeight: 600,
    border: "1px solid #e0e0e0",
    color: "#444",
    backgroundColor: "#fafafa",
    transition: "all 0.15s ease",
    "&:hover": {
      backgroundColor: "#f7f7f7",
    },
  },
  modeButtonActive: {
    backgroundColor: "#fff8dc",
    borderColor: THEME_COLORS.yellow,
    color: "#222",
  },
  chatPanel: {
    borderRadius: "16px",
    border: "1px solid #f5e8b4",
    backgroundColor: "#fffaf0",
    padding: "16px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "14px",
  },
  chatHeader: {
    display: "flex",
    alignItems: "flex-start" as const,
    justifyContent: "space-between" as const,
    gap: "16px",
    flexWrap: "wrap" as const,
  },
  chatMessages: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "12px",
    maxHeight: "320px",
    overflowY: "auto" as const,
    padding: "12px",
    backgroundColor: "#fff",
    borderRadius: "12px",
    border: "1px solid #f1f1f1",
  },
  chatMessageRow: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "6px",
  },
  chatMessageAuthor: {
    fontSize: "11px",
    fontWeight: 700,
    color: "#555",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
    fontFamily: "Lexend, sans-serif",
  },
  chatBubble: {
    padding: "12px 14px",
    borderRadius: "14px",
    backgroundColor: "#f7f7f7",
    border: "1px solid #e8e8e8",
    maxWidth: "100%",
  },
  chatInputBar: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap" as const,
  },
  // Prompt area
  promptArea: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "4px",
  },
  promptInput: {
    width: "100%",
    minHeight: "100px",
    padding: "14px 16px",
    boxSizing: "border-box" as const,
    border: "1px solid #e8e8e8",
    borderRadius: "12px",
    outline: "none",
    fontFamily: "Lexend, sans-serif",
    fontSize: "14px",
    resize: "none" as const,
    backgroundColor: "#fafafa",
    color: "#222",
    transition: "border-color 0.15s",
    "&::placeholder": {
      color: "#bbb",
    },
    "&:focus": {
      borderColor: THEME_COLORS.yellow,
      backgroundColor: "#fff",
    },
  },
  // Bottom toolbar
  formToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "10px",
  },
  generateButton: {
    backgroundColor: `${THEME_COLORS.yellow} !important`,
    color: "#000 !important",
    fontFamily: "Lexend, sans-serif !important",
    fontWeight: "600 !important",
    borderRadius: "10px !important",
    padding: "8px 22px !important",
    fontSize: "14px !important",
    textTransform: "none !important" as "none",
    "&:hover": {
      backgroundColor: `${THEME_COLORS.darkYellow} !important`,
    },
    "&:disabled": {
      opacity: "0.5 !important",
    },
  },
  // Empty state
  emptyState: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "32px",
    opacity: 0.5,
  },
  // Modal video
  modalVideo: {
    width: "100%",
    maxHeight: "70vh",
    borderRadius: "8px",
    display: "block",
    backgroundColor: "#000",
  },
  modalStatusArea: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 24px",
    gap: "8px",
  },
}));

export default useStyles;
